import { useEffect, useRef, useState, useCallback } from 'react'
import { getWsUrl } from '../constants/config'

// ─── Voice WebSocket hook ────────────────────────────────────────────────────
//
// Talks to the backend voice loop at /ws/voice. Protocol is defined in
// backend/src/voice/contracts.ts — we code the client against those exact shapes:
//
//   client → server : auth | start | audio (base64 PCM16 16kHz mono) | text | stop
//   server → client : ready | transcript | audio (base64 PCM16 24kHz mono) | tool | error
//
// Audio assumptions (from VOICE_AUDIO in contracts.ts):
//   - mic INPUT  → PCM16, mono, 16_000 Hz   (we capture + downsample to this)
//   - tts OUTPUT → PCM16, mono, 24_000 Hz   (we decode + play this back)
//
// Capture strategy: we use a ScriptProcessorNode rather than an AudioWorklet.
// AudioWorklet is the modern API but requires shipping a separate worklet module
// file and wiring it through the Vite bundler (audioContext.audioWorklet.addModule).
// ScriptProcessorNode keeps all logic in this one file, is supported everywhere
// the app runs, and the per-chunk processing here is light (Float32 → Int16 +
// linear-interpolation downsample). It is deprecated but not removed; if it ever
// is, the fallback is a worklet module exposing the same Float32 frames.

// ─── Types ─────────────────────────────────────────────────────────────────────

export type VoiceState = 'idle' | 'connecting' | 'listening' | 'speaking' | 'error'

export interface VoiceTranscript {
  role:  'user' | 'assistant'
  text:  string
  final: boolean
}

export interface VoiceToolStatus {
  name:     string
  status:   'start' | 'done'
  summary?: string
}

export interface UseVoiceWSOptions {
  workspaceId?: string
  chatId?:      string
}

export interface UseVoiceWSReturn {
  state:       VoiceState
  transcripts: VoiceTranscript[]
  toolStatus:  VoiceToolStatus | null
  error:       string | null
  start:       () => void
  stop:        () => void
}

// Server → client message shape (mirrors VoiceServerMsg in contracts.ts).
type VoiceServerMsg =
  | { type: 'ready' }
  | { type: 'transcript'; role: 'user' | 'assistant'; text: string; final: boolean }
  | { type: 'audio';  data: string }
  | { type: 'tool';   name: string; status: 'start' | 'done'; summary?: string }
  | { type: 'error';  message: string }

// ─── Audio constants (must match VOICE_AUDIO in contracts.ts) ──────────────────

const INPUT_SAMPLE_RATE  = 16_000  // mic chunks we SEND
const OUTPUT_SAMPLE_RATE = 24_000  // tts chunks we RECEIVE
// ScriptProcessor frame size. 4096 @ ~mic-rate ≈ 85ms before downsampling — a
// reasonable trade-off between latency and per-chunk overhead.
const CAPTURE_BUFFER_SIZE = 4096

// ─── PCM / base64 helpers ──────────────────────────────────────────────────────

/** Derive the /ws/voice URL from the configured chat WS URL by swapping the path. */
function voiceWsUrl(): string {
  const base = getWsUrl()
  try {
    const u = new URL(base)
    u.pathname = '/ws/voice'
    return u.toString()
  } catch {
    // base wasn't a full URL — fall back to a naive path replace.
    return base.replace(/\/ws.*$/, '/ws/voice')
  }
}

/**
 * Downsample a Float32 mono buffer from `inRate` to `outRate` and convert to
 * little-endian PCM16. Uses linear interpolation; good enough for speech STT.
 * Samples are clamped to [-1, 1] before the 16-bit scale.
 */
function floatTo16BitPCM(input: Float32Array, inRate: number, outRate: number): ArrayBuffer {
  if (outRate === inRate) {
    const out = new Int16Array(input.length)
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]))
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    return out.buffer
  }

  const ratio       = inRate / outRate
  const outLength   = Math.round(input.length / ratio)
  const out         = new Int16Array(outLength)
  for (let i = 0; i < outLength; i++) {
    const pos    = i * ratio
    const left   = Math.floor(pos)
    const right  = Math.min(left + 1, input.length - 1)
    const frac   = pos - left
    const sample = input[left] * (1 - frac) + input[right] * frac
    const s      = Math.max(-1, Math.min(1, sample))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out.buffer
}

/** Base64-encode an ArrayBuffer without blowing the call stack on large buffers. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary  = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK)
    binary += String.fromCharCode.apply(null, slice as unknown as number[])
  }
  return btoa(binary)
}

/** Decode base64 → Float32 mono samples, assuming little-endian PCM16 input. */
function base64PCM16ToFloat32(b64: string): Float32Array {
  const binary = atob(b64)
  const len    = binary.length
  const bytes  = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)

  // Reinterpret the bytes as Int16 little-endian. Use a DataView so we don't
  // depend on the platform's native endianness (most are LE, but be explicit).
  const view    = new DataView(bytes.buffer)
  const samples = new Float32Array(len >> 1)
  for (let i = 0; i < samples.length; i++) {
    const int16 = view.getInt16(i * 2, true /* little-endian */)
    samples[i] = int16 < 0 ? int16 / 0x8000 : int16 / 0x7fff
  }
  return samples
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useVoiceWS(
  token: string | null,
  { workspaceId, chatId }: UseVoiceWSOptions = {},
): UseVoiceWSReturn {
  const [state, setState]             = useState<VoiceState>('idle')
  const [transcripts, setTranscripts] = useState<VoiceTranscript[]>([])
  const [toolStatus, setToolStatus]   = useState<VoiceToolStatus | null>(null)
  const [error, setError]             = useState<string | null>(null)

  // WS + audio graph refs (not state — they shouldn't trigger re-renders).
  const wsRef          = useRef<WebSocket | null>(null)
  const micStreamRef   = useRef<MediaStream | null>(null)
  const captureCtxRef  = useRef<AudioContext | null>(null)
  const sourceNodeRef  = useRef<MediaStreamAudioSourceNode | null>(null)
  const processorRef   = useRef<ScriptProcessorNode | null>(null)

  // Separate AudioContext for playback at the 24kHz output rate so scheduled
  // buffers don't fight the capture context's rate.
  const playbackCtxRef = useRef<AudioContext | null>(null)
  // Monotonic schedule cursor: when the next queued chunk should start, so chunks
  // play back-to-back with no gaps.
  const playheadRef    = useRef<number>(0)
  // Count of in-flight (scheduled but not yet ended) playback nodes, to flip the
  // UI state back from 'speaking' to 'listening' when TTS audio drains.
  const playingCountRef = useRef<number>(0)

  // Keep the latest props available inside callbacks without re-subscribing.
  const tokenRef       = useRef(token)
  const workspaceIdRef = useRef(workspaceId)
  const chatIdRef      = useRef(chatId)
  useEffect(() => { tokenRef.current = token },             [token])
  useEffect(() => { workspaceIdRef.current = workspaceId }, [workspaceId])
  useEffect(() => { chatIdRef.current = chatId },           [chatId])

  // ─── Playback: schedule one decoded PCM16 chunk gaplessly ──────────────────

  const enqueuePlayback = useCallback((samples: Float32Array) => {
    let ctx = playbackCtxRef.current
    if (!ctx) {
      // Pin the context to the output sample rate so the raw PCM plays at pitch.
      ctx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
      playbackCtxRef.current = ctx
    }
    // Resume if the context was suspended (autoplay policy / tab backgrounding).
    if (ctx.state === 'suspended') void ctx.resume()

    if (samples.length === 0) return

    const buffer = ctx.createBuffer(1, samples.length, OUTPUT_SAMPLE_RATE)
    // getChannelData returns a Float32Array view we can fill directly; this
    // sidesteps copyToChannel's stricter Float32Array<ArrayBuffer> overload.
    buffer.getChannelData(0).set(samples)

    const node = ctx.createBufferSource()
    node.buffer = buffer
    node.connect(ctx.destination)

    // Schedule contiguously: never start before "now", otherwise continue the
    // existing stream from the running playhead.
    const now   = ctx.currentTime
    const start = Math.max(now, playheadRef.current)
    node.start(start)
    playheadRef.current = start + buffer.duration

    playingCountRef.current += 1
    setState('speaking')
    node.onended = () => {
      playingCountRef.current -= 1
      // When the queue drains, return to listening (mic is still open in toggle
      // mode). If the mic was stopped, stop() will have set the state already.
      if (playingCountRef.current <= 0 && micStreamRef.current) {
        setState('listening')
      }
    }
  }, [])

  // ─── Tear down the mic capture graph (keep WS open for playback) ───────────

  const teardownCapture = useCallback(() => {
    processorRef.current?.disconnect()
    processorRef.current = null
    sourceNodeRef.current?.disconnect()
    sourceNodeRef.current = null
    micStreamRef.current?.getTracks().forEach(t => t.stop())
    micStreamRef.current = null
    if (captureCtxRef.current && captureCtxRef.current.state !== 'closed') {
      void captureCtxRef.current.close()
    }
    captureCtxRef.current = null
  }, [])

  // ─── Full teardown (WS + capture + playback) ───────────────────────────────

  const teardownAll = useCallback(() => {
    teardownCapture()
    if (playbackCtxRef.current && playbackCtxRef.current.state !== 'closed') {
      void playbackCtxRef.current.close()
    }
    playbackCtxRef.current = null
    playheadRef.current = 0
    playingCountRef.current = 0

    const ws = wsRef.current
    wsRef.current = null
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'stop' })) } catch { /* noop */ }
      ws.close()
    }
  }, [teardownCapture])

  // ─── Start the mic capture graph and pipe PCM16 chunks over the WS ─────────

  const startCapture = useCallback(async () => {
    // getUserMedia is gated behind a secure context (https / localhost).
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    micStreamRef.current = stream

    // The browser usually opens the mic at 44.1k/48k; we downsample to 16k in
    // floatTo16BitPCM. We don't force a sampleRate on the context because most
    // browsers ignore the hint and resample anyway.
    const ctx = new AudioContext()
    captureCtxRef.current = ctx
    if (ctx.state === 'suspended') await ctx.resume()

    const source = ctx.createMediaStreamSource(stream)
    sourceNodeRef.current = source

    const processor = ctx.createScriptProcessor(CAPTURE_BUFFER_SIZE, 1, 1)
    processorRef.current = processor

    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      // Mono: channel 0 only.
      const input  = e.inputBuffer.getChannelData(0)
      const pcm    = floatTo16BitPCM(input, ctx.sampleRate, INPUT_SAMPLE_RATE)
      const data   = arrayBufferToBase64(pcm)
      ws.send(JSON.stringify({ type: 'audio', data }))
    }

    // ScriptProcessorNode only fires onaudioprocess while connected to the graph.
    // Routing through destination keeps it pulling; the node itself is silent.
    source.connect(processor)
    processor.connect(ctx.destination)
  }, [])

  // ─── Public: start the voice session ───────────────────────────────────────

  const start = useCallback(() => {
    if (!tokenRef.current) {
      setError('Not authenticated')
      setState('error')
      return
    }
    // Already running.
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return

    setError(null)
    setTranscripts([])
    setToolStatus(null)
    setState('connecting')

    const ws = new WebSocket(voiceWsUrl())
    wsRef.current = ws

    ws.onopen = () => {
      // Match the existing WS auth pattern: auth first, then start.
      ws.send(JSON.stringify({ type: 'auth', token: tokenRef.current }))
      ws.send(JSON.stringify({
        type:        'start',
        workspaceId: workspaceIdRef.current,
        chatId:      chatIdRef.current,
      }))
    }

    ws.onmessage = (event) => {
      let msg: VoiceServerMsg
      try { msg = JSON.parse(event.data as string) as VoiceServerMsg } catch { return }

      switch (msg.type) {
        case 'ready':
          // Server is ready — open the mic and start streaming.
          setState('listening')
          startCapture().catch((err: unknown) => {
            setError(err instanceof Error ? err.message : 'Microphone access failed')
            setState('error')
          })
          break

        case 'transcript':
          setTranscripts(prev => {
            // Coalesce consecutive non-final updates from the same role into one
            // live line, then "freeze" it once final arrives.
            const last = prev[prev.length - 1]
            if (last && last.role === msg.role && !last.final) {
              const next = prev.slice(0, -1)
              next.push({ role: msg.role, text: msg.text, final: msg.final })
              return next
            }
            return [...prev, { role: msg.role, text: msg.text, final: msg.final }]
          })
          break

        case 'audio':
          enqueuePlayback(base64PCM16ToFloat32(msg.data))
          break

        case 'tool':
          setToolStatus({ name: msg.name, status: msg.status, summary: msg.summary })
          break

        case 'error':
          setError(msg.message)
          setState('error')
          break
      }
    }

    ws.onerror = () => {
      setError('Voice connection error')
      setState('error')
    }

    ws.onclose = () => {
      // Tear down capture; leave any already-scheduled playback to finish.
      teardownCapture()
      // Only fall back to idle if we weren't parked in an error state.
      setState(prev => (prev === 'error' ? prev : 'idle'))
    }
  }, [startCapture, enqueuePlayback, teardownCapture])

  // ─── Public: stop the voice session ────────────────────────────────────────

  const stop = useCallback(() => {
    teardownAll()
    setState('idle')
  }, [teardownAll])

  // ─── Cleanup on unmount ────────────────────────────────────────────────────

  useEffect(() => {
    return () => { teardownAll() }
  }, [teardownAll])

  return { state, transcripts, toolStatus, error, start, stop }
}
